import type { ScratchpadEntry } from "./machine";

export type ScratchpadAuthor = Pick<ScratchpadEntry, "identityProviderId" | "reference" | "concurrency"> & {
  updated: string;
};

// Other authors with a scratchpad, keyed by identityProviderId (lowercased on fetch) and inserted alphabetically.
export type ScratchpadAuthorIndex = ReadonlyMap<string, ScratchpadAuthor>;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

// Built by the machine once per response: `siblings` lists every saved scratchpad, including the owner's.
export function indexScratchpadAuthors(
  siblings: ReadonlyArray<Omit<ScratchpadEntry, "value">>,
  ownerId: string,
): ScratchpadAuthorIndex {
  const result = new Map<string, ScratchpadAuthor>();

  for (const sibling of siblings
    .filter((sibling) => sibling.identityProviderId !== ownerId)
    .sort((a, b) => a.identityProviderId.localeCompare(b.identityProviderId))) {
    result.set(sibling.identityProviderId, {
      identityProviderId: sibling.identityProviderId,
      reference: sibling.reference,
      concurrency: sibling.concurrency,
      updated: sibling.updated ?? sibling.created,
    });
  }

  return result;
}

export function formatTimeSince(timestamp: string | null, now: number): string | null {
  if (timestamp == null) return null;

  const then = new Date(timestamp).getTime();
  const diff = now - then;
  if (diff < 45_000) return "just now";
  if (diff < HOUR) return `${Math.max(1, Math.floor(diff / MINUTE))}m ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff < WEEK) return `${Math.floor(diff / DAY)}d ago`;
  if (diff < 5 * WEEK) return `${Math.floor(diff / WEEK)}w ago`;

  const date = new Date(then);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}
